'use client';

/* eslint-disable react-hooks/rules-of-hooks */
import React, {
  useContext,
  useState,
  useEffect,
  PropsWithChildren,
} from 'react';
import fscreen from 'fscreen';
import { CaptionSource } from '@osmosis/shared/types/learn';

import { VIDEO_SHORTCUTS } from '../constants/videoShortcuts';
import type ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
import { VideoEnum } from '../types/video-player';

const SEEK_AMOUNT = 10; // seconds

// eslint-disable-next-line no-console
fscreen.onfullscreenerror = (error) => console.error(error);

export const playbackSpeeds = [0.5, 0.8, 1, 1.3, 1.5, 1.8, 2, 2.5, 3, 3.5, 4];

export const shortcuts: KeyboardShortcut[] = VIDEO_SHORTCUTS;

type FilePlayerValue = ReactPlayer | undefined;

export type KeyboardShortcut = {
  key: string;
  description: string;
  isIcon?: boolean;
};

export type VideoContextProps = {
  getPlayer: () => FilePlayerValue;
  setPlayer: (player: ReactPlayer) => void;
  playing: boolean;
  handlePause: () => void;
  handlePlay: () => void;
  handlePlayPause: () => void;
  volume: number;
  setVolume: (value: number) => void;
  playbackRate: number;
  setPlaybackRate: (value: number) => void;
  isFullscreen: boolean;
  setIsFullscreen: (value: boolean) => void;
  startedState: [boolean, (newIsStarted: boolean) => void];
  activeCaptions?: CaptionSource;
  setActiveCaptions: (captions?: CaptionSource) => void;
  shortcuts: KeyboardShortcut[];
  isPictureInPicture: boolean;
  handlePictureInPicture: () => void;
  setPictureInPicture: (value: boolean) => void;
  handleFullscreen: () => void;
  handleSeekTo: (position: number) => void;
  hotkeysEnabled: boolean;
  setHotkeysEnabled: (enabled: boolean) => void;
  setCaptions: (captions: CaptionSource[] | undefined) => void;
  isReady: boolean;
  hotkeyMap: Record<string, string>;
  hotkeyHandlers: Record<string, (event: KeyEvent) => void>;
  isStarted: boolean;
  type: VideoEnum;
  progressState: OnProgressProps;
  setProgressState: (progress: OnProgressProps) => void;
  buffering: boolean;
  setBuffering: (value: boolean) => void;
};

const VideoPlayerContext = React.createContext<VideoContextProps>({
  player: undefined,
  setPlayer: () => {
    return undefined;
  },
  getPlayer: () => {
    return undefined;
  },
  playing: false,
  setPlaying: () => {
    return undefined;
  },
  handlePlayPause: () => undefined,
  startedState: [
    false,
    (): void => {
      return undefined;
    },
  ],

  volume: 1,
  setVolume: () => {
    return undefined;
  },
  playbackRate: 1,
  setPlaybackRate: () => {
    return undefined;
  },
  isFullscreen: false,
  setIsFullscreen: () => {
    return undefined;
  },
  activeCaptions: undefined,
  setActiveCaptions: () => {
    return undefined;
  },
  shortcuts: [],
  handleFullscreen: () => {
    return undefined;
  },
  setCaptions: () => {
    return undefined;
  },
  isReady: false,
  isStarted: false,
  progressState: {
    loaded: 0,
    played: 0,
    loadedSeconds: 0,
    playedSeconds: 0,
  },
  setProgressState: () => {
    return undefined;
  },
  buffering: false,
  setBuffering: () => {
    return undefined;
  },
} as unknown as VideoContextProps);

type KeyEvent = KeyboardEvent | undefined;

/**
 * Provider that stores video player methods and state.
 */
export const VideoPlayerProvider = (
  props: PropsWithChildren<{
    player?: ReactPlayer;
    type: VideoEnum;
  }>,
): JSX.Element => {
  const { player: initialPlayer, type, children } = props;
  const [player, setPlayerObject] = useState(initialPlayer);
  const [isReady, setIsReady] = useState(!!initialPlayer);
  const [playing, setPlaying] = useState(false);
  const startedState = useState(false);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [progressState, setProgressState] = useState({
    loaded: 0,
    played: 0,
    loadedSeconds: 0,
    playedSeconds: 0,
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPictureInPicture, setIsPictureInPicture] = useState(false);
  const [hotkeysEnabled, setHotkeysEnabled] = useState(true);
  const [activeCaptions, setActiveCaptions] = useState<
    CaptionSource | undefined
  >();
  const [captions, setCaptions] = useState<CaptionSource[] | undefined>();

  const [isStarted, setIsStarted] = startedState;
  const [buffering, setBuffering] = useState(false);

  const handlePlay = () => {
    if (!isStarted) setIsStarted(true);

    const internalPlayer = player?.getInternalPlayer();

    if (internalPlayer) {
      if (type === VideoEnum.YOUTUBE) {
        // YouTube internal player uses "playVideo" not "play"
        internalPlayer.playVideo();
        // YouTube does not fire event
        setPlaying(true);
      } else {
        internalPlayer.play();
      }
    }
  };

  const handlePause = () => {
    const internalPlayer = player?.getInternalPlayer();
    if (internalPlayer) {
      if (type === VideoEnum.YOUTUBE) {
        // YouTube internal player uses "pauseVideo", not "pause"
        internalPlayer.pauseVideo();
        // YouTube does not fire event
        setPlaying(false);
      } else {
        internalPlayer.pause();
      }
    }
  };

  /**
   * Toggle play or paused.
   */
  const handlePlayPause = (): void => {
    if (!isStarted) setIsStarted(true);
    if (playing) handlePause();
    else handlePlay();
  };

  /**
   * Toggle play / pause from hotkey.
   */
  const handleHotkeyPlayPause = (e: KeyEvent): void => {
    e?.preventDefault();
    handlePlayPause();
  };

  /**
   * Toggle picture in picture.
   */
  const handlePictureInPicture = () => {
    setIsPictureInPicture(!isPictureInPicture);
  };

  const setPictureInPicture = (value: boolean) => {
    setIsPictureInPicture(value);
  };

  /**
   * TODO: caption keypress.
   */
  const handleCaptions = () => {
    if (activeCaptions) return setActiveCaptions(undefined);
    if (captions) setActiveCaptions(captions[0]);
  };

  /**
   * Change playback speed.
   */
  const handlePlaybackSpeed = (nextIndex: number): void => {
    const currentIndex = playbackSpeeds.findIndex(
      (rate) => playbackRate === rate,
    );
    const nextSpeed = playbackSpeeds[currentIndex + nextIndex];
    if (nextSpeed) setPlaybackRate(nextSpeed);
  };

  /**
   * Seek to value.
   */
  const handleSeekTo = (value: number) => {
    player?.seekTo(value);
  };

  /**
   * Seek to percentage.
   */
  const handleSeekPercentage = (percentage: number) => {
    const seekVal = (player?.getDuration() || 0) * percentage;
    handleSeekTo(seekVal);
  };

  /**
   * Change volume.
   */
  const handleSetVolume = (value: number): void => {
    setVolume((prevVolume) => {
      if (prevVolume + value > 1) return 1;
      else if (prevVolume + value < 0) return 0;
      else return prevVolume + value;
    });
  };

  /**
   * Get the player object from ref.
   */
  const getPlayer = (): FilePlayerValue => {
    return player;
  };

  /**
   * Set the player object to ref.
   */
  const setPlayer = (newPlayer: ReactPlayer): void => {
    setPlayerObject(newPlayer);

    if (newPlayer) {
      setIsReady(true);
    }
  };

  const handleFullscreenChange = React.useCallback(() => {
    if (fscreen.fullscreenElement !== null) {
      setIsFullscreen(true);
    } else {
      setIsFullscreen(false);
    }
  }, []);

  const preventScroll = (event: KeyboardEvent) => {
    // workaround to prevent spacebar from scrolling the page
    // when using hotkeys
    if (
      (event.key === ' ' || event.code === 'space') &&
      event.target === document.getElementById('video-player-container')
    ) {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener('fullscreenchange', handleFullscreenChange);

      return () => {
        fscreen.removeEventListener('fullscreenchange', handleFullscreenChange);
      };
    }
  }, [handleFullscreenChange]);

  const handleFullscreen = () => {
    if (type === VideoEnum.YOUTUBE) {
      // Handle YouTube iframe API to fullscreen
      if (!isStarted) setIsStarted(true);

      const iframe = player?.getInternalPlayer()?.getIframe?.();
      const requestedFullScreen =
        iframe?.requestFullScreen ||
        iframe?.mozRequestFullScreen ||
        iframe?.webkitRequestFullScreen;

      if (requestedFullScreen) {
        requestedFullScreen.bind(iframe)();
      }
    } else {
      // Otherwise use fscreen
      const containerEl =
        document.getElementById('video-player-container') || undefined;

      const videoEl = document.querySelectorAll('video') || undefined;

      containerEl?.focus();

      if (fscreen.fullscreenElement) {
        fscreen.exitFullscreen();
      } else {
        setTimeout(() => {
          if (containerEl && fscreen.fullscreenEnabled) {
            fscreen.requestFullscreen(containerEl);
          } else if (videoEl[0]) {
            // https://elsevier.atlassian.net/browse/OSMS-4341
            videoEl[0].webkitEnterFullscreen();
          }
        }, 500);
      }
    }
  };

  let keyMap: { [index: string]: string } = {};
  if (hotkeysEnabled) {
    keyMap = {
      FULL_SCREEN: 'f',
      PLAY_PAUSE: 'space',
      PLAY_PAUSE_K: 'k',
      CAPTIONS: 'c',
      INCREASE_PLAYBACK: '=',
      DECREASE_PLAYBACK: '-',
      BACKWARD: 'left',
      FORWARD: 'right',
      VOLUME_UP: 'up',
      VOLUME_DOWN: 'down',
    };
  }

  const handlers: { [index: string]: (e: KeyEvent) => void } = {
    FULL_SCREEN: (e: KeyEvent) => {
      e?.preventDefault();
      handleFullscreen();
    },
    PLAY_PAUSE: (e) => handleHotkeyPlayPause(e),
    PLAY_PAUSE_K: () => handlePlayPause(),
    CAPTIONS: handleCaptions,
    INCREASE_PLAYBACK: () => handlePlaybackSpeed(-1),
    DECREASE_PLAYBACK: () => handlePlaybackSpeed(1),
    BACKWARD: (e: KeyEvent) => {
      e?.preventDefault();
      if (player) {
        handleSeekTo(player?.getCurrentTime() - SEEK_AMOUNT);
      }
    },
    FORWARD: (e: KeyEvent) => {
      e?.preventDefault();
      if (player) {
        handleSeekTo(player?.getCurrentTime() + SEEK_AMOUNT);
      }
    },
    VOLUME_UP: (e: KeyEvent) => {
      e?.preventDefault();
      handleSetVolume(0.1);
    },
    VOLUME_DOWN: (e: KeyEvent) => {
      e?.preventDefault();
      handleSetVolume(-0.1);
    },
  };

  ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].forEach((key) => {
    if (hotkeysEnabled) keyMap[key] = key;
    handlers[key] = () => handleSeekPercentage(+key / 10);
  });

  // Use native element play / pause events to handle
  // playing state (do not use setPlaying)
  const handlePlayEvent = () => {
    setPlaying(true);
  };

  const handlePauseEvent = () => {
    setPlaying(false);
  };

  useEffect(() => {
    if (player) {
      const videoEl = player.getInternalPlayer();
      videoEl.addEventListener('play', handlePlayEvent);
      videoEl.addEventListener('pause', handlePauseEvent);
      document.addEventListener('keydown', preventScroll);
    }

    return () => {
      if (player) {
        const videoEl = player.getInternalPlayer();
        videoEl?.removeEventListener('play', handlePlayEvent);
        videoEl?.removeEventListener('pause', handlePauseEvent);
        document.removeEventListener('keydown', preventScroll);
      }
    };
  }, [player]);

  return (
    <VideoPlayerContext.Provider
      value={{
        playing,
        startedState,
        volume,
        setVolume,
        playbackRate,
        setPlaybackRate,
        getPlayer,
        setPlayer,
        isFullscreen,
        setIsFullscreen,
        activeCaptions,
        setActiveCaptions,
        shortcuts,
        handlePlayPause,
        handlePlay,
        handlePause,
        isPictureInPicture,
        handlePictureInPicture,
        setPictureInPicture,
        handleFullscreen,
        handleSeekTo,
        hotkeysEnabled,
        setHotkeysEnabled,
        setCaptions,
        setProgressState,
        progressState,
        isReady,
        hotkeyMap: keyMap,
        hotkeyHandlers: handlers,
        isStarted,
        type,
        buffering,
        setBuffering,
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  );
};

/**
 * Hook to retrieve video player context.
 */
export const useVideoPlayer = () => useContext(VideoPlayerContext);
